import {
  CallingMethod,
  CodecProtocol,
  Device,
  MonitoringLevel,
  MonitoringSource,
} from "@services/devices/types";
import {
  KeyofDevice,
  KeysOfDevice,
  RequiredKeysOfDevice,
  fieldIsEmpty,
} from "./const";
import { DeviceType } from "@common/components/ListDetailViews/DetailView/DeviceCard/const";
import { getAllRequiredFields as getLocationAllRequiredFields } from "@common/utils/locations/validation";
import { getAllRequiredFields as getRoomAllRequiredFields } from "@common/utils/rooms/validation";
import {
  getErrorFromLocationFields,
  getErrorFromRoomFields,
} from "./validation";
import { isUUID } from "@services/devices";
import { DeviceModelDetail } from "@services/catalog/types";

export const alwaysRequiredFields: KeyofDevice[] = [
  "type",
  "category",
  "manufacturer",
  "model",
  "monitoringLevel",
];
export const getRequiredFieldsDueToCallCapable = (
  device: Device
): KeyofDevice[] => (device.callCapable ? ["protocol", "callingMethod"] : []);

export const getRequiredFieldsDueToProtocol = (
  device: Device
): KeyofDevice[] => {
  if (!device.callCapable || device.callingMethod == CallingMethod.None)
    return [];
  if (
    device.callingMethod === CallingMethod.H323IP ||
    device.callingMethod === CallingMethod.SIPIP
  )
    return ["ipAddress"];

  if (
    device.protocol === CodecProtocol.SIPOnly ||
    device.callingMethod === CallingMethod.SIPURI
  ) {
    return ["sipRegistrar", "sipUri"];
  }
  if (
    device.protocol === CodecProtocol.H323Only ||
    device.callingMethod === CallingMethod.H323E164
  ) {
    return ["gatekeeperIp", "e164"];
  }
  if (
    device.protocol === CodecProtocol.DualH323SIP &&
    device.callingMethod === CallingMethod.Auto
  ) {
    return ["sipRegistrar", "sipUri", "gatekeeperIp", "e164"];
  }
  if (device.protocol === CodecProtocol.ISDN) {
    return ["e164"];
  }
  if (device.protocol === CodecProtocol.MSTeams) {
    return ["msTeamsId"];
  }
  return [];
};
export const getRequiredFieldsDueToManagementInterface = (
  device: Device
): KeyofDevice[] => {
  if ((device.details?.managementInterfaces?.length ?? 0) > 0)
    return ["ipAddress"];
  return [];
};
export const getRequiredFieldsDueToCategory = (
  device: Device
): KeyofDevice[] => {
  let requiredFields: KeyofDevice[] = [];
  switch (device.category) {
    case "Telepresence":
      requiredFields.push("details.position");
    case "Single Codecs":
      requiredFields.push("callingMethod");
      requiredFields.push("protocol");
      if (
        [CallingMethod.H323IP, CallingMethod.SIPIP].includes(
          device.callingMethod ?? CallingMethod.None
        )
      )
        requiredFields.push("ipAddress");
      return requiredFields;
    case "MCU":
      return ["details.totalPorts", "details.sipDialInDomain"];
    case "Proxy":
      return ["macAddress"];
    default:
      return [];
  }
};
export const getRequiredFieldsDueToMonitoringLevel = (
  device: Device
): KeyofDevice[] => {
  switch (device.monitoringLevel) {
    case MonitoringLevel.All:
    case MonitoringLevel.Ping:
      return ["monitoringSources"];
    case MonitoringLevel.None:
    default:
      return [];
  }
};

export const getLocationRequiredFields = (device: Device) => {
  return device.room?.location?.id
    ? []
    : getErrorFromLocationFields(
        getLocationAllRequiredFields(device.room?.location)
      );
};

export const getRoomRequiredField = (device: Device) => {
  return device.room?.id && isUUID(device.room.id)
    ? []
    : getErrorFromRoomFields(getRoomAllRequiredFields(device.room));
};

export const getRequiredFieldsDueToType = (device: Device): KeyofDevice[] => {
  if (device.type == DeviceType.ASSET) {
    return ["name"];
  }
  return [];
};

export const getRequiredFieldsDueToMonitoringSource = (
  device: Device,
  modelDatas: Partial<DeviceModelDetail> = {}
): RequiredKeysOfDevice => {
  if (
    device.monitoringSources === undefined ||
    fieldIsEmpty(device.monitoringSources)
  )
    return [];
  let requiredFields: RequiredKeysOfDevice = [];
  let atLeastOne: KeysOfDevice = [];
  let atLeastOnePropertyMapped =
    propertyMapped("macAddress", modelDatas) ||
    propertyMapped("serialNumber", modelDatas);

  if (
    device.monitoringSources.find((x) =>
      [
        MonitoringSource.RoomController,
        MonitoringSource._3rdPartyManagement,
        MonitoringSource.AVISPLMonitoringAgent,
      ].includes(x)
    )
  ) {
    if (!atLeastOnePropertyMapped) atLeastOne.push("macAddress");
    if (!atLeastOnePropertyMapped) atLeastOne.push("serialNumber");
    if (
      !device.monitoringSources.includes(MonitoringSource.AVISPLMonitoringAgent)
    ) {
      atLeastOnePropertyMapped =
        atLeastOnePropertyMapped || propertyMapped("thirdPartyId", modelDatas);
      if (!atLeastOnePropertyMapped) atLeastOne.push("thirdPartyId");
    }
    if (
      device.monitoringSources.find((x) =>
        [
          MonitoringSource.RoomController,
          MonitoringSource._3rdPartyManagement,
        ].includes(x)
      )
    ) {
      requiredFields.push("aggregator.id");
    }
  }
  if (device.monitoringSources.includes(MonitoringSource.CommProxy)) {
    if (!propertyMapped("ipAddress", modelDatas))
      requiredFields.push("ipAddress");
    requiredFields.push("pingProtocol");
    if (device.monitoringLevel === MonitoringLevel.All)
      requiredFields.push("details.managementInterfaces");
  }
  if (atLeastOne.length > 0) requiredFields.push(atLeastOne);
  return requiredFields;
};

export const getRequiredFieldDueToPingProtocol = (device: Device) => {
  let requiredFields: RequiredKeysOfDevice = [];
  if (device.pingProtocol === "TCP") {
    requiredFields.push("details.managementInterfaces");
  }
  return requiredFields;
};

export const propertyMapped = (
  key: string | KeyofDevice | KeysOfDevice,
  modelDatas?: DeviceModelDetail
) => {
  return modelDatas?.mappedExtendedProperties?.[key as string] !== undefined;
};

export const isRequiredField = (
  device: Device,
  key: KeyofDevice,
  modelDatas: Partial<DeviceModelDetail> = {}
) =>
  getAllRequiredFields(device, false, false, modelDatas).flat().includes(key);
export const getAllRequiredFields = (
  device: Device,
  includeLocationFields: boolean = false,
  includeRoomFields: boolean = false,
  modelDatas: Partial<DeviceModelDetail> = {}
) =>
  [
    alwaysRequiredFields,
    getRequiredFieldsDueToCallCapable(device),
    getRequiredFieldsDueToProtocol(device),
    getRequiredFieldsDueToMonitoringLevel(device),
    getRequiredFieldsDueToManagementInterface(device),
    getRequiredFieldsDueToMonitoringSource(device, modelDatas),
    getRequiredFieldsDueToCategory(device),
    getRequiredFieldsDueToType(device),
    getRequiredFieldDueToPingProtocol(device),
    includeLocationFields ? getLocationRequiredFields(device) : [],
    includeRoomFields ? getRoomRequiredField(device) : [],
  ]
    .flat()
    .filter((property) => !propertyMapped(property, modelDatas));
